import eyeIcon from '@/assets/icons/icon_eye.svg'
import eyeSlashIcon from '@/assets/icons/icon_eye_slash.svg'
import { forwardRef, useId, useMemo, useState } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import cls from './input.module.css'

export type InputSize = 'large' | 'small' | 'free'

export type UIInputProps = {
  label?: string
  hint?: string
  error?: string | boolean
  leftAddon?: ReactNode
  rightAddon?: ReactNode
  clearable?: boolean
  size?: InputSize
  className?: string
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>

const Input = forwardRef<HTMLInputElement, UIInputProps>(function Input(
  {
    id,
    label,
    hint,
    error,
    leftAddon,
    rightAddon,
    clearable,
    size = 'large',
    className = '',
    onChange,
    value,
    defaultValue,
    disabled,
    ...rest
  },
  ref,
) {
  const internalId = useId()
  const inputId = id ?? internalId
  const hasError = Boolean(error)

  const rootClass = useMemo(() => {
    return [cls.root, cls[size], disabled ? cls.disabled : '', hasError ? cls.error : '', className].filter(Boolean).join(' ')
  }, [size, disabled, hasError, className])

  const showClear = clearable && !disabled && typeof value === 'string' && value.length > 0
  const inputType = rest.type as string | undefined
  const isPasswordField = inputType === 'password'
  const [passwordVisible, setPasswordVisible] = useState(false)
  const effectiveType = isPasswordField && passwordVisible ? 'text' : inputType

  const rightElements: ReactNode[] = []
  if (isPasswordField) {
    rightElements.push(
      <button
        key="toggle-visibility"
        type="button"
        aria-label={passwordVisible ? 'Hide password' : 'Show password'}
        className={cls.eyeButton}
        onClick={() => setPasswordVisible((v) => !v)}
        tabIndex={-1}
      >
        <img
          src={passwordVisible ? eyeIcon : eyeSlashIcon}
          alt={passwordVisible ? 'Hide password' : 'Show password'}
          className={cls.eyeIcon}
        />
      </button>,
    )
  }
  if (rightAddon) rightElements.push(<span key="addon">{rightAddon}</span>)

  return (
    <div className={rootClass}>
      {label ? (
        <label htmlFor={inputId} className={cls.label}>
          {label}
        </label>
      ) : null}

      <div className={cls.fieldWrapper}>
        {leftAddon ? <div className={cls.addonLeft}>{leftAddon}</div> : null}

        <input
          id={inputId}
          ref={ref}
          className={cls.input}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          aria-invalid={hasError || undefined}
          aria-describedby={hint ? `${inputId}-hint` : undefined}
          {...rest}
          type={effectiveType}
        />

        {showClear ? (
          <button
            type="button"
            aria-label="Clear input"
            className={cls.clear}
            onClick={(e) => {
              const target = e.currentTarget.previousElementSibling as HTMLInputElement | null
              if (target && onChange) {
                const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set
                if (nativeInputValueSetter) nativeInputValueSetter.call(target, '')
                const ev = new Event('input', { bubbles: true })
                target?.dispatchEvent(ev)
              }
            }}
          >
            ×
          </button>
        ) : null}

        {rightElements.length > 0 ? <div className={cls.addonRight}>{rightElements}</div> : null}
      </div>

      {hint ? (
        <div id={`${inputId}-hint`} className={cls.hint}>
          {hint}
        </div>
      ) : null}
      {typeof error === 'string' ? <div className={cls.errorText}>{error}</div> : null}
    </div>
  )
})

export default Input
